import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PanelContent from '@shared/components/PanelContent';
import { useMapStore } from '@shared/hooks/useMapStore';
import { usePanelTitle } from '@app/layout/usePanelTitle';
import RouteList from './components/RouteList';
import RouteCreationPanel from './components/RouteCreationPanel';

type View = 'list' | 'create';

export default function RoutesPage() {
  const [view, setView] = useState<View>('list');
  const navigate = useNavigate();

  const setActiveProductsRouteId = useMapStore((s) => s.setActiveProductsRouteId);
  const setSelectedRouteId = useMapStore((s) => s.setSelectedRouteId);
  const { setPanelTitle } = usePanelTitle();

  // Clear route products from map when the route list page is shown
  useEffect(() => {
    setActiveProductsRouteId(null);
  }, [setActiveProductsRouteId]);

  // Set breadcrumbs in panel toolbar
  useEffect(() => {
    if (view === 'create') {
      setPanelTitle(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              cursor: 'pointer',
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
            onClick={handleBackToList}
          >
            Routes
          </Typography>
          <NavigateNextIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          <Typography variant="subtitle2" fontWeight={600}>
            Create Route
          </Typography>
        </Box>,
      );
    } else {
      setPanelTitle(null);
    }
    return () => setPanelTitle(null);
  }, [view, setPanelTitle]);

  function handleSelectRoute(routeId: string) {
    setSelectedRouteId(routeId);
  }

  /** Navigate to the route details (products on route) page */
  function handleShowProducts(routeId: string) {
    setActiveProductsRouteId(routeId);
    setSelectedRouteId(routeId);
    navigate(`/routes/${routeId}/details`);
  }

  function handleCreateRoute() {
    setView('create');
  }

  function handleBackToList() {
    setView('list');
  }

  return (
    <div data-testid="page-routes" className="flex flex-col h-full">
      {view === 'list' && (
        <PanelContent>
          <RouteList
            onSelectRoute={handleSelectRoute}
            onShowProducts={handleShowProducts}
            onCreateRoute={handleCreateRoute}
          />
        </PanelContent>
      )}

      {view === 'create' && <RouteCreationPanel onBack={handleBackToList} />}
    </div>
  );
}
